import { authorizedAxios, authorizedMultpartAxios, campaignAuthorizedAxios, publicAxios } from "./axiosConfig";
import defaultImage from '../../default.jpg'

const profileImageCache = new Map<number, string>();
const profileImageInFlight = new Map<number, Promise<string>>();

const Api = {
    attachProfileImages: async (items: any[] = []) => {
        if (!items?.length) return [];
        const imagePromises = items.map((item) => Api.getProfileImage(item.id));
        const images = await Promise.all(imagePromises);
        images.forEach((img, index) => {
            items[index].img = img;
        });
        return items;
    },
    getUserWithProfile: async (response: any) => {
        const hasEnvelope = response?.data && typeof response.data === 'object' && response.data.data;
        const envelope = hasEnvelope ? response.data : null;
        const authData = hasEnvelope ? envelope.data : response.data;

        const { id, accessToken } = authData || {};
        if (!accessToken || envelope?.error) return response;

        localStorage.setItem("accessToken", accessToken)
        const profile = await Api.getProfileDetails(id)

        if (hasEnvelope) {
            response.data = {
                ...envelope,
                data: {
                    ...authData,
                    profile,
                },
            };
            return response;
        }

        response.data.profile = profile
        return response
    },
    signIn: async (payload: any) => {
        payload = { ...payload, deviceType: 'WEB' }
        const signInUrl = '/v2/auth/sign-in';
        const response = await publicAxios.post(signInUrl, payload);
        return await Api.getUserWithProfile(response);
    },
    sendSignInOTP: async (payload: any) => {
        const url = '/v2/auth/send/otp/v1';
        return await publicAxios.post(url, payload);
    },
    verifySignInOTP: async (payload: any) => {
        const url = '/v2/auth/sign-in/otp/v1';
        const response = await publicAxios.post(url, payload);
        return await Api.getUserWithProfile(response);
    },
    validateToken: async () => {
        const url = "/v1/users/validate-token";
        const response = await authorizedAxios.post(url, {});
        return await Api.getUserWithProfile(response);
    },
    getStaticData: async () => {
        const url = "/v2/static-data";
        return await publicAxios.get(url);
    },
    searchProfiles: async ({ limit, page, payload }: any) => {
        const response = await authorizedAxios.post(`/v2/search?limit=${limit}&page=${page}`, payload)
        return response
    },
    getAllProfiles: async ({ limit, page, listName, isInit, hasProfileImage }: any) => {
        if (listName === 'search' && isInit) {
            return { data: { items: [], currentPage: page, itemPerPage: limit, totalItems: 0, } }
        }
        const preference = sessionStorage.getItem('preference')
        if (listName === 'search' && preference) {
            const payload = JSON.parse(preference)
            return await Api.searchProfiles({ limit, page, payload })
        }
        const params: string[] = [];
        if (hasProfileImage) params.push('hasProfileImage=true');
        const querySuffix = params.length ? `&${params.join('&')}` : '';

        let url = "";
        if (listName === 'all' || listName === 'search')
            url = `/v2/profiles?limit=${limit}&page=${page}${querySuffix}`;
        if (listName === 'viewers')
            url = `/v2/who-viewed-me?limit=${limit}&page=${page}${querySuffix}`;
        if (listName === 'sent')
            url = `/v2/interest-sent?limit=${limit}&page=${page}${querySuffix}`;
        if (listName === 'received')
            url = `/v2/interest-received?limit=${limit}&page=${page}${querySuffix}`;
        if (listName === 'recent')
            url = `/v2/recently-viewed-by-me?limit=${limit}&page=${page}${querySuffix}`;
        const response = await authorizedAxios.get(url);
        return response
    },
    getProfileImage: async (id: number) => {
        if (profileImageCache.has(id)) {
            return profileImageCache.get(id) as string;
        }

        if (profileImageInFlight.has(id)) {
            return await (profileImageInFlight.get(id) as Promise<string>);
        }

        const url = `/v1/galleries/profile-image/${id}`;
        const request = (async () => {
            const res = await authorizedAxios.get(url);
            const { fileType, content } = res.data || {};
            const image = (fileType && content)
                ? `data:${fileType};base64,${content}`
                : defaultImage;
            profileImageCache.set(id, image);
            return image;
        })();

        profileImageInFlight.set(id, request);
        try {
            return await request;
        } finally {
            profileImageInFlight.delete(id);
        }
    },
    getFile: async (fileName: string, fileType: string, userId: number) => {
        const url = `/v1/galleries/file/${userId}/${fileName}`
        const res = await authorizedAxios.get(url);
        const { file } = res.data;
        return `data:${fileType};base64,${file}`
    },
    getGalleryImages: async (userId: number) => {
        const url = `/v1/galleries/list/${userId}`;
        const res = await authorizedAxios.get(url);
        const imgRequests = [];
        if (res?.data?.length) {
            for (const item of res.data) {
                const { fileName, fileType } = item;
                imgRequests.push(Api.getFile(fileName, fileType, userId))
            }
        }
        const images = await Promise.all(imgRequests);
        return images.map((image, index) => {
            return {
                id: res?.data[index]?.id || index + 1,
                alt_description: "Image",
                url: image
            }
        })
    },
    getProfileDetails: async (userId: number) => {
        const url = `/v1/profiles/getByUserId/${userId}`;
        const reqests = [
            authorizedAxios.get(url),
            Api.getProfileImage(userId)
        ]
        const [profile, image]: any = await Promise.all(reqests)
        const safeProfile = profile?.data && typeof profile.data === 'object'
            ? { ...profile.data }
            : {};
        safeProfile['img'] = image;
        return safeProfile;
    },
    updatePofileDetails: async (payload: any) => {
        const url = `/v1/profiles`;
        const response = await authorizedAxios.post(url, payload);
        return response;
    },
    changePassword: async (payload: any) => {
        const url = `/v1/users/changePassword`;
        return await authorizedAxios.post(url, payload)
    },
    deleteAccount: async () => {
        const url = `/v1/users/delete-account`;
        return await authorizedAxios.post(url, {});
    },
    compressImage: (dataUri: string, w: number, h: number) => {
        return new Promise((resolve, reject) => {
            try {
                const image = new Image();
                image.src = dataUri;
                image.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = w;
                    canvas.height = h;
                    const ctx = canvas.getContext('2d');
                    if (ctx) ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
                    resolve({ error: false, imageUri: canvas.toDataURL('image/png') })
                }
            } catch (error: any) {
                reject({ error: true, message: error.message })
            }
        })
    },
    base64ToBinary(base64String: string) {
        const binaryString = atob(base64String.split(',')[1]);
        const length = binaryString.length;
        const binaryArray = new Uint8Array(length);
        for (let i = 0; i < length; i++)
            binaryArray[i] = binaryString.charCodeAt(i);
        return new Blob([binaryArray], { type: 'image/jpeg' });
    },
    updateProfileImage: async (payload: { file: Blob, userId: string }) => {
        const { file, userId } = payload;
        const formData = new FormData();
        formData.append('file', file, `profile_image_${userId}.jpg`)
        formData.append('userId', userId);
        formData.append('galleryType', 'PROFILE_IMAGE');
        const response = await authorizedMultpartAxios.post('/v1/galleries', formData)
        if (response.status === 200) {
            const numericUserId = parseInt(userId);
            profileImageCache.delete(numericUserId);
            const img = await Api.getProfileImage(numericUserId)
            return { img, status: 200 }
        }
        return response
    },
    uploadGallaryImage: async (payload: { file: Blob, userId: string }) => {
        const { file, userId } = payload;
        const formData = new FormData();
        formData.append('file', file, `gallary_image_${userId}.jpg`)
        formData.append('userId', userId);
        formData.append('galleryType', 'GALLERY_IMAGE');
        const response = await authorizedMultpartAxios.post('/v1/galleries', formData)
        if (response.status === 200) {
            const images = await Api.getGalleryImages(parseInt(userId))
            return { images, status: 200 }
        }
        return response
    },
    trashImage: async (payload: { imageId: number, userId: number }) => {
        const { imageId, userId } = payload;
        const deleteApiUrl = `/v1/galleries/${imageId}`;
        return await authorizedAxios.delete(deleteApiUrl)
    },
    getPayment: async (userId: number) => {
        const apiUrl = `/v1/packages/getByUserId/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    sendSignUpOTP: async (payload: { mobile: string }) => {
        const apiUrl = `/v2/auth/send/otp/v1`;
        return await publicAxios.post(apiUrl, payload);
    },
    verifySignUpOTP: async (payload: any) => {
        const apiUrl = '/v2/auth/verify/otp/v1';
        const response = await publicAxios.post(apiUrl, payload);
        return await Api.getUserWithProfile(response);
    },
    setViewed: async (profileId: number) => {
        const apiUrl = `/v2/set-viewed/${profileId}`;
        return await authorizedAxios.post(apiUrl, {})
    },
    sendInterest: async (profileId: number) => {
        const apiUrl = `/v2/set-interest/${profileId}`;
        return await authorizedAxios.post(apiUrl, {})
    },
    sendReportOrNote: async (type: 'NOTE' | 'REPORT', payload: any) => {
        const apiUrl = `/v1/interests/send-report-or-note/${type}`;
        return await authorizedAxios.post(apiUrl, payload);
    },
    getInterest: async (fromId: number, toId: number) => {
        const apiUrl = `/v1/interests/get-interest/${fromId}/${toId}`;
        return await authorizedAxios.get(apiUrl);
    },
    getContactListByUserId: async (userId: number, deviceId?: number) => {
        const params = deviceId ? `?deviceId=${deviceId}` : '';
        const apiUrl = `/v1/contacts/${userId}${params}`;
        return await campaignAuthorizedAxios.get(apiUrl);
    },
    getDeviceListByUserId: async (userId: number) => {
        const apiUrl = `/v1/contacts/devices/${userId}`;
        return await campaignAuthorizedAxios.get(apiUrl);
    },
    createContactList: async (payload: { userId: number; deviceId?: number | null; contactNumberList: Array<{ contactName?: string; contactNumber?: string }> }) => {
        const apiUrl = `/v1/contacts/`;
        return await campaignAuthorizedAxios.post(apiUrl, payload);
    },
    getAllContacts: async (page: number, size: number) => {
        const apiUrl = `/v1/contacts/getAllContacts/${page}/${size}`;
        return await campaignAuthorizedAxios.post(apiUrl, {});
    },
    getAllContactDevices: async (
        page: number,
        size: number,
        userId?: number,
        nativeCountry?: number,
        nativeState?: number,
        nativeCity?: number,
        currentCountry?: number,
        currentState?: number,
        currentCity?: number,
        downloadedSort?: string,
    ) => {
        const params = new URLSearchParams();
        if (userId != null) params.set('userId', String(userId));
        if (nativeCountry != null) params.set('nativeCountry', String(nativeCountry));
        if (nativeState != null) params.set('nativeState', String(nativeState));
        if (nativeCity != null) params.set('nativeCity', String(nativeCity));
        if (currentCountry != null) params.set('currentCountry', String(currentCountry));
        if (currentState != null) params.set('currentState', String(currentState));
        if (currentCity != null) params.set('currentCity', String(currentCity));
        if (downloadedSort) params.set('downloadedSort', downloadedSort);

        const apiUrl = `/v1/contacts/getAllContactDevices/${page}/${size}${params.toString() ? `?${params.toString()}` : ''}`;
        return await campaignAuthorizedAxios.post(apiUrl, {});
    },
    markDeviceDownloaded: async (deviceId: number, userId?: number) => {
        const params = new URLSearchParams();
        if (userId != null) params.set('userId', String(userId));
        params.set('deviceId', String(deviceId));
        const apiUrl = `/v1/contacts/markDeviceDownloaded${params.toString() ? `?${params.toString()}` : ''}`;
        return await campaignAuthorizedAxios.post(apiUrl, {});
    },
    getMyNotes: async (userId: number) => {
        const apiUrl = `/v1/interests/notes/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    deleteMyNote: async (fromId: number, toId: number) => {
        const apiUrl = `/v1/interests/delete-note`;
        return await authorizedAxios.post(apiUrl, { fromId, toId });
    },
    // Interest endpoints with timestamps
    getInterestSent: async (userId: number) => {
        const apiUrl = `/v1/interests/sent/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    getInterestReceived: async (userId: number) => {
        const apiUrl = `/v1/interests/received/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    getInterestRejected: async (userId: number) => {
        const apiUrl = `/v1/interests/rejected/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    sendInterestConfirm: async (fromId: number, toId: number) => {
        const apiUrl = `/v1/interests/send`;
        return await authorizedAxios.post(apiUrl, { fromId, toId });
    },
    updateInterestState: async (fromId: number, toId: number, state: string) => {
        const apiUrl = `/v1/interests/update-state`;
        return await authorizedAxios.post(apiUrl, { fromId, toId, state });
    },
    cancelInterest: async (fromId: number, toId: number) => {
        const apiUrl = `/v1/interests/cancel/${fromId}/${toId}`;
        return await authorizedAxios.post(apiUrl, {});
    },
    // My Book endpoints
    addToMyBook: async (userId: number, profileId: number, notes?: string) => {
        const apiUrl = `/v1/mybook/add`;
        return await authorizedAxios.post(apiUrl, { userId, profileId, notes: notes || '' });
    },
    removeFromMyBook: async (userId: number, profileId: number) => {
        const apiUrl = `/v1/mybook/remove/${userId}/${profileId}`;
        return await authorizedAxios.delete(apiUrl);
    },
    getMyBook: async (userId: number, page: number = 0, size: number = 10) => {
        const apiUrl = `/v1/mybook/${userId}?page=${page}&size=${size}`;
        return await authorizedAxios.get(apiUrl);
    },
    isInMyBook: async (userId: number, profileId: number) => {
        const apiUrl = `/v1/mybook/check/${userId}/${profileId}`;
        return await authorizedAxios.get(apiUrl);
    },
    updateMyBookNotes: async (userId: number, profileId: number, notes: string) => {
        const apiUrl = `/v1/mybook/notes/${userId}/${profileId}`;
        return await authorizedAxios.put(apiUrl, { notes });
    },
    getMyBookCount: async (userId: number) => {
        const apiUrl = `/v1/mybook/count/${userId}`;
        return await authorizedAxios.get(apiUrl);
    },
    debounce: <T extends (...args: any[]) => void>(
        func: T,
        delay: number
    ) => {
        let timeout: NodeJS.Timeout;
        return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), delay);
        };
    },
    getToday: () => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const today = new Date();
        const day = today.getDate();
        const month = months[today.getMonth()];
        const year = today.getFullYear();
        return `${day}-${month}-${year}`;
    },
    ccAvenueReq: async (payload: any) => {
        const apiUrl = `/v1/ccavenue/requestHandler`;
        return await authorizedAxios.post(apiUrl, payload)
    }
}
export default Api;